import { Fragment, useMemo } from "react";
import Error from "@mui/icons-material/Error";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Warning from "@mui/icons-material/Warning";
import { Box, Icon, Typography, useTheme } from "@mui/material";
import { isArray } from "lodash-es";

import type { IconType } from "../../types/subText.js";

export interface HelperTextProps {
  subtext?: string;
  errorMessage?: string | string[];
  warningMessage?: string;
  noteMessage?: string;
}

export const HelperText = (props: HelperTextProps) => {
  const theme = useTheme();

  const messageType = ((props.errorMessage && "error") ??
    (props.warningMessage && "warning") ??
    (props.noteMessage && "note") ??
    "info") as IconType | "note";

  const color = (() => {
    if (messageType === "error") return theme.palette.error.main;
    if (messageType === "warning") return theme.palette.warning.main;
    if (messageType === "note") return theme.palette.text.primary;
    if (messageType === "info") return theme.palette.text.primary;
    return theme.palette.text.primary;
  })();

  const dataTestId = useMemo(() => {
    if (messageType === "error") return "helper-text-error";
    if (messageType === "warning") return "helper-text-warning";
    if (messageType === "note") return "helper-text-note";
    if (messageType === "info") return "helper-text-info";
  }, [messageType]);

  const renderMultiLineErrorMessage = () => {
    const errorMessage = props.errorMessage as string[];
    return (
      <>
        {errorMessage.map((line: string, index: number) => (
          <Fragment key={index}>
            {line}
            <br />
          </Fragment>
        ))}
      </>
    );
  };

  return (
    <Box
      className={messageType === "error" ? "thalamosErrorMessage" : ""}
      data-testid={dataTestId}
      sx={{
        display: "flex",
        flexGrow: 1,
        marginTop: theme.spacing(0.5),
        color: color,
      }}
    >
      <Icon style={{ display: "flex" }} component="div">
        {messageType === "error" && (
          <Error data-testid="error" sx={{ color: color }} />
        )}
        {messageType === "warning" && (
          <Warning data-testid="warning" sx={{ color: color }} />
        )}
        {messageType === "info" && (
          <InfoOutlined data-testid="info" sx={{ color: color }} />
        )}
        {messageType === "note" && (
          <Box
            sx={{
              backgroundColor: theme.palette.info.main,
              borderRadius: "50%",
              width: theme.spacing(3),
              height: theme.spacing(3),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InfoOutlined
              data-testid="note"
              sx={{ color: "#fff", fontSize: theme.spacing(2.25) }}
            />
          </Box>
        )}
      </Icon>
      <Typography
        sx={{
          fontSize: theme.spacing(1.75),
          alignSelf: "start",
          marginLeft: theme.spacing(1),
        }}
      >
        {props.errorMessage && isArray(props.errorMessage)
          ? renderMultiLineErrorMessage()
          : props.errorMessage ||
            props.warningMessage ||
            props.noteMessage ||
            props.subtext}
      </Typography>
    </Box>
  );
};
